import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { 
  LayoutDashboard, 
  Users, 
  MapPin, 
  Clock, 
  Bell, 
  LogOut,
  Menu,
  X,
  ExternalLink,
  FileBarChart,
  ClipboardCheck
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_baduwork-tracker/artifacts/8tdmywqb_Badu%20Facility%20Services.png";

const navItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
  { icon: Users, label: "Trabalhadores", path: "/admin/workers" },
  { icon: MapPin, label: "Locais", path: "/admin/locations" },
  { icon: Clock, label: "Registros", path: "/admin/records" },
  { icon: ClipboardCheck, label: "Checklists", path: "/admin/checklists" },
  { icon: FileBarChart, label: "Relatórios", path: "/admin/reports" },
  { icon: Bell, label: "Notificações", path: "/admin/notifications" },
];

export default function AdminLayout({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const NavItem = ({ icon: Icon, label, path, badge }) => {
    const isActive = location.pathname === path;
    
    return (
      <button
        onClick={() => {
          navigate(path);
          setMobileMenuOpen(false);
        }}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all relative ${
          isActive 
            ? "bg-gradient-to-r from-green-50 to-amber-50 text-[#F5A623] border-l-4 border-[#F5A623]" 
            : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
        }`}
        data-testid={`nav-${path.split("/").pop() || "dashboard"}`}
      >
        <Icon size={20} />
        <span className="font-medium">{label}</span>
        {badge > 0 && (
          <span className="notification-badge ml-auto">{badge}</span>
        )}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Gradient bar at top */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2ECC40] to-[#F5A623] z-50"></div>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex sidebar flex-col pt-1">
        {/* Logo */}
        <div className="p-6 border-b border-gray-100">
          <img 
            src={LOGO_URL} 
            alt="Badu Facility Services" 
            className="h-12 mx-auto"
            data-testid="sidebar-logo"
          />
          <p className="text-center text-sm font-bold gradient-text mt-2">PONTO FACIL</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavItem 
              key={item.path} 
              {...item} 
              badge={item.path === "/admin/notifications" ? unreadCount : 0}
            />
          ))}
        </nav>

        {/* User & Logout */}
        <div className="p-4 border-t border-gray-100">
          <div className="flex items-center gap-3 px-4 py-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#2ECC40] to-[#F5A623] flex items-center justify-center text-white font-bold">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">{user?.name}</p>
              <p className="text-xs text-gray-500">Administrador</p>
            </div>
          </div>
          <a 
            href="https://www.badufacility.ch/pontofacil"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-500 hover:text-[#2ECC40] transition-colors mb-2"
          >
            <ExternalLink size={14} />
            badufacility.ch
          </a>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-gray-500 hover:text-red-500 hover:bg-red-50"
            data-testid="logout-btn"
          >
            <LogOut size={18} className="mr-3" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-1 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-2">
            <img 
              src={LOGO_URL} 
              alt="Badu" 
              className="h-8"
            />
            <span className="font-bold gradient-text text-sm">PONTO FACIL</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-gray-600"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-30 bg-gray-900/20" onClick={() => setMobileMenuOpen(false)}>
          <div className="absolute top-20 left-0 right-0 bg-white border-b border-gray-100 shadow-lg p-4" onClick={e => e.stopPropagation()}>
            <nav className="space-y-1">
              {navItems.map((item) => (
                <NavItem 
                  key={item.path} 
                  {...item} 
                  badge={item.path === "/admin/notifications" ? unreadCount : 0}
                />
              ))}
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="w-full justify-start text-gray-500 hover:text-red-500 hover:bg-red-50 mt-4"
              >
                <LogOut size={18} className="mr-3" />
                Sair
              </Button>
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="main-content md:ml-0 pt-20 md:pt-1">
        <div className="p-6 md:p-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
